import { Eye } from 'lucide-react';

import { HelpTooltip } from '@/components/ui/help';
import { Badge, CheckboxTable, EmptyState } from '@/components/ui';
import type { Column } from '@/components/ui';
import { ChannelBadges } from '@/components/ui/ChannelBadges';
import { isBroadcastQueueRowSelectable } from '@/features/broadcast-queue/selectors';
import { getQueueSourceLabelForQueueItem } from '@/features/broadcast-queue/queueSourceRegistry';
import type { BroadcastMode, BroadcastQueueItem } from '@/types/broadcastQueue';
import {
  channelDisplayNames,
  getBroadcastQueueStatusDetail,
  statusColors,
  statusLabels,
} from '@/types/broadcastQueue';

type AppointmentLabel = {
  dateTimeLabel: string;
  doctorName: string;
  branchLabel?: string;
  clinicAddress?: string;
};

type BroadcastQueueTableProps = {
  items: BroadcastQueueItem[];
  mode: BroadcastMode;
  searchQuery: string;
  canUseSelection: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onPreview: (itemId: string) => void;
};

function formatDate(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getQueueField(item: BroadcastQueueItem, ...keys: string[]) {
  for (const key of keys) {
    const variableValue = item.variables?.[key];
    if (typeof variableValue === 'string' && variableValue.trim()) {
      return variableValue.trim();
    }

    const metadataValue = item.metadata?.[key];
    if (typeof metadataValue === 'string' && metadataValue.trim()) {
      return metadataValue.trim();
    }
  }
  return '';
}

function getBranchBadgeClassName(branchLabel: string) {
  const normalized = branchLabel.trim().toLowerCase();
  if (normalized === 'филиал 1') {
    return 'bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-200';
  }
  if (normalized === 'филиал 2') {
    return 'bg-sky-100 text-sky-800 ring-1 ring-inset ring-sky-200';
  }
  return 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200';
}

function getAppointmentLabel(item: BroadcastQueueItem): AppointmentLabel | null {
  const appointmentDate = getQueueField(item, 'appointment_date', 'APPOINTMENT_DATE');
  const appointmentTime = getQueueField(item, 'appointment_time', 'APPOINTMENT_TIME');
  const doctorName = getQueueField(item, 'doctor_name', 'DOCTOR_NAME');
  const branchLabel = getQueueField(item, 'branch_label');
  const branchKey = getQueueField(item, 'branch_key');
  const filialId = getQueueField(item, 'filial_id');
  const clinicAddress = getQueueField(item, 'clinic_address', 'CLINIC_ADDRESS');

  if (!appointmentDate && !appointmentTime && !doctorName && !branchLabel && !branchKey && !filialId) {
    return null;
  }

  const dateTimeLabel = [appointmentDate, appointmentTime].filter(Boolean).join(' ');
  let branchDisplay = branchLabel;
  if (!branchDisplay && filialId) {
    branchDisplay = `Филиал ${filialId}`;
  } else if (!branchDisplay && branchKey) {
    branchDisplay = branchKey;
  }

  return {
    dateTimeLabel: dateTimeLabel || '—',
    doctorName: doctorName || '—',
    branchLabel: branchDisplay,
    clinicAddress,
  };
}

function getPatientLabel(item: BroadcastQueueItem) {
  return getQueueField(item, 'patient_fio', 'PATIENT_FIO') || item.patient_name || '';
}

function getClusterLabel(item: BroadcastQueueItem) {
  return getQueueField(item, 'cluster_name', 'CLUSTER_NAME');
}

// 24.03.26: таблицу и cell-formatting вынесли из page-оркестратора.
export function BroadcastQueueTable({
  items,
  mode,
  searchQuery,
  canUseSelection,
  selectedIds,
  onSelectionChange,
  onPreview,
}: BroadcastQueueTableProps) {
  const columns: Column<BroadcastQueueItem>[] = [
    {
      key: 'event_type',
      header: 'Тип',
      render: (item) => {
        const clusterLabel = item.event_type === 'cluster_followup' ? getClusterLabel(item) : '';
        return (
          <div className="space-y-1">
            <Badge variant="info" className="text-xs whitespace-nowrap">
              {getQueueSourceLabelForQueueItem(item) || item.event_type}
            </Badge>
            {clusterLabel && (
              <div className="text-xs text-gray-500 leading-4 max-w-[14rem]" title={clusterLabel}>
                {clusterLabel}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'patient_name',
      header: 'Пациент',
      render: (item) => (
        <div className="space-y-1">
          <span className="font-medium text-gray-900">{getPatientLabel(item) || '—'}</span>
          {item.available_channels && (
            <ChannelBadges
              channels={item.available_channels}
              size="sm"
              showOnlyAvailable
              showChannels={['telegram_bot', 'max', 'max_client', 'telegram_client', 'sms', 'sms_tel', 'whatsapp_waba', 'whatsapp_regular']}
              className="max-w-[16rem]"
            />
          )}
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Телефон',
      render: (item) => (
        <span className="font-mono text-gray-600 text-sm">{item.phone}</span>
      ),
    },
    {
      key: 'channel',
      header: 'Канал',
      render: (item) => {
        const channel = item.channel || 'none';
        const metadata = item.metadata as Record<string, unknown> | undefined;
        const isResend = metadata?.is_resend as boolean | undefined;
        const wasChanged = metadata?.channel_changed_from as string | undefined;
        return (
          <div className="flex items-center gap-1">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                channel === 'telegram_bot'
                  ? 'bg-blue-100 text-blue-800'
                  : channel === 'telegram_client'
                    ? 'bg-cyan-100 text-cyan-800'
                    : channel === 'whatsapp_waba'
                      ? 'bg-green-100 text-green-800'
                      : channel === 'whatsapp_regular'
                        ? 'bg-emerald-100 text-emerald-800'
                        : channel === 'sms'
                          ? 'bg-purple-100 text-purple-800'
                          : channel === 'sms_tel'
                            ? 'bg-slate-100 text-slate-800'
                          : channel === 'max'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-800'
              }`}
            >
              {channelDisplayNames[channel] || channel}
            </span>
            {wasChanged && (
              <span
                className="text-xs text-amber-600"
                title={`Изменён с ${channelDisplayNames[wasChanged] || wasChanged}`}
              >
                ✎
              </span>
            )}
            {isResend && (
              <span className="text-xs text-blue-600" title="Повторная отправка">
                🔄
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'rendered_text',
      header: 'Сообщение',
      className: 'max-w-xs',
      render: (item) => (
        <span className="text-gray-600 text-sm truncate block max-w-xs" title={item.rendered_text || ''}>
          {item.rendered_text || '—'}
        </span>
      ),
    },
    {
      key: 'appointment_slot',
      header: 'На когда запись',
      render: (item) => {
        const appointment = getAppointmentLabel(item);
        if (!appointment) {
          return <span className="text-gray-400 text-sm">—</span>;
        }
        return (
          <div className="text-sm">
            <div className="font-medium text-gray-900">{appointment.dateTimeLabel}</div>
            <div className="text-gray-500">к {appointment.doctorName}</div>
            {appointment.branchLabel && (
              <div className="flex flex-wrap items-center gap-1.5 text-gray-500">
                <span
                  className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${getBranchBadgeClassName(appointment.branchLabel)}`}
                >
                  {appointment.branchLabel}
                </span>
                {appointment.clinicAddress && <span>{appointment.clinicAddress}</span>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Статус',
      render: (item) => {
        const statusDetail = getBroadcastQueueStatusDetail(item);
        return (
        <div>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[item.status]}`}>
            {statusLabels[item.status]}
          </span>
          {statusDetail && <p className="text-xs text-gray-500 mt-1">{statusDetail}</p>}
          {item.reject_reason && <p className="text-xs text-red-500 mt-1">{item.reject_reason}</p>}
        </div>
        );
      },
    },
    {
      key: 'created_at',
      header: 'Создано',
      render: (item) => (
        <span className="text-gray-500 text-sm">{formatDate(item.created_at)}</span>
      ),
    },
    {
      key: 'scheduled_at',
      header: 'Расписание',
      render: (item) => (
        <span className="text-gray-500 text-sm">{formatDate(item.scheduled_at)}</span>
      ),
    },
    {
      key: 'preview',
      header: '',
      render: (item) => (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview(item.id);
            }}
            className="p-1.5 rounded-md text-neutral-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            title="Предпросмотр доставки"
          >
            <Eye className="w-4 h-4" />
          </button>
          <HelpTooltip
            side="left"
            content="Открывает проверку перед отправкой: текст сообщения, кнопки, SMS-ссылку, выбранный канал и варианты смены канала."
          />
        </div>
      ),
    },
  ];

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-8">
        <EmptyState
          title="Очередь пуста"
          description={
            searchQuery
              ? `По запросу «${searchQuery}» ничего не найдено`
              : mode === 'manual'
                ? 'Рассылки будут появляться здесь при запуске шедулера'
                : 'В автоматическом режиме рассылки отправляются сразу'
          }
        />
      </div>
    );
  }

  if (canUseSelection) {
    return (
      <div className="bg-white rounded-lg border overflow-hidden">
        <CheckboxTable
          data={items}
          columns={columns}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          getRowId={(item) => item.id}
          isRowDisabled={(item) => !isBroadcastQueueRowSelectable(item)}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50">
              {columns.map((col) => (
                <td key={col.key} className={`px-4 py-3 text-sm ${col.className || ''}`}>
                  {col.render(item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
